import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  ConsentSchema,
  SignConsentSchema,
  type Consent,
  type ProcedureId,
  type SignConsentInput,
} from '@datazo/contracts';
import { ApiClientError, apiFetch } from '@/shared/api/client';

/**
 * Hooks de acceso a `/api/consent`. El consentimiento es legalmente vinculante
 * — siempre validamos la respuesta del backend contra `ConsentSchema`.
 */

export const consentKeys = {
  all: ['consent'] as const,
  byProcedure: (id: ProcedureId) => ['consent', id] as const,
};

/**
 * Devuelve el consentimiento firmado de un episodio, o `null` si aún no está
 * firmado (404). Tratar el 404 como `null` evita pasar por `isError` en la UI
 * — el caso "no firmado todavía" es esperado.
 */
export function useConsentByProcedure(
  procedimientoId: ProcedureId | undefined,
): UseQueryResult<Consent | null, Error> {
  return useQuery({
    queryKey:
      procedimientoId !== undefined
        ? consentKeys.byProcedure(procedimientoId)
        : (['consent', '__missing__'] as const),
    queryFn: async (): Promise<Consent | null> => {
      if (procedimientoId === undefined) {
        throw new Error('procedimientoId requerido');
      }
      try {
        return await apiFetch<Consent>(
          `/api/consent/by-procedure/${encodeURIComponent(procedimientoId)}`,
          { parseAs: ConsentSchema },
        );
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: procedimientoId !== undefined,
    staleTime: 30_000,
  });
}

export function useSignConsent(): UseMutationResult<Consent, Error, SignConsentInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SignConsentInput) => {
      const body = SignConsentSchema.parse(input);
      return apiFetch<Consent>('/api/consent/sign', {
        method: 'POST',
        body,
        parseAs: ConsentSchema,
      });
    },
    onSuccess: (created) => {
      qc.setQueryData(consentKeys.byProcedure(created.procedimientoId), created);
      void qc.invalidateQueries({ queryKey: consentKeys.byProcedure(created.procedimientoId) });
      void qc.invalidateQueries({ queryKey: ['procedures'] });
      void qc.invalidateQueries({ queryKey: ['procedures', created.procedimientoId] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export type { Consent };
